import type { Account } from '@omni/classes/account/account.class';
import type { Contact } from '@omni/classes/contact/contact.class';
import type { Brand } from '@omni/classes/brand/brand.class';

/**
 * Type definitions for Rx Sales Data service.
 * Contains enums, interfaces, and type definitions used throughout
 * the Rx Sales Data synchronization and aggregation system.
 */

/**
 * Enum representing different time periods for sales data aggregation.
 * Used to categorize sales data by various time windows.
 */
export enum Period {
  CW = 1,    // Current Week
  C3W = 2,  // Current 3 Weeks
  C4W = 9,  // Current 4 Weeks
  C6W = 3,  // Current 6 Weeks
  C8W = 10, // Current 8 Weeks
  C13W = 11, // Current 13 Weeks
  CM = 4,   // Current Month
  C3M = 5,  // Current 3 Months
  C6M = 6,  // Current 6 Months
  QTD = 7,  // Quarter to Date
  YTD = 8,  // Year to Date
}

/**
 * Enum representing different metrics for sales data measurement.
 * Used to categorize various types of sales performance indicators.
 */
export enum Metric {
  TRx = 1,        // Total Prescriptions
  NRx = 2,        // New Prescriptions
  Refills = 3,    // Prescription Refills
  SalesValue = 4, // Sales Value (monetary)
  SalesVolume = 5 // Sales Volume (quantity)
}

/**
 * Base data structure containing a metric value and its change from the previous period.
 * Used as a building block for more complex data structures.
 */
export type Data = {
  value: number;  // Current metric value
  change: number; // Change from previous period
}

/**
 * Data structure for sales-related metrics.
 * Contains volume and value metrics for sales performance tracking.
 */
export type SalesMetrics = {
  salesVolume?: Data; // Sales volume metrics (quantity-based)
  salesValue?: Data;  // Sales value metrics (monetary)
}

/**
 * Data structure for prescription-related metrics.
 * Contains metrics for tracking prescription performance and refill rates.
 */
export type RxMetrics = {
  NRx?: Data;     // New prescriptions data
  TRx?: Data;     // Total prescriptions data
  Refills?: Data; // Prescription refills data
}

/**
 * Interface representing a geographical brick/territory.
 * Contains identification and coordinate information for mapping purposes.
 */

export type Brick = {
  brickId: string;
  brickName: string;
}

export type BrickWithCoordinates = Brick & {
  coordinates: [number, number][][][];
}

/**
 * Base data structure for sales data.
 * Contains product and period information.
 */
export type BaseSalesData = {
  /**
   * The product/brand.
   */
  product: Brand;
  /**
   * The time period for the data.
   */
  period: Period;
}

/**
 * Data structure for brick-based sales data.
 * Contains brick information and sales data (volume/value).
 */
export type BrickSalesData = BaseSalesData & SalesMetrics & {
  type: 'brick';
  brick: Brick;
}

/**
 * Data structure for account-based direct sales data.
 * Contains account information and sales data (volume/value).
 */
export type DirectSalesData = BaseSalesData & SalesMetrics & {
  type: 'direct';
  account: Account;
}

/**
 * Data structure for contact-based prescription data.
 * Contains contact information and prescription data (NRx/TRx/Refills).
 */
export type RxData = BaseSalesData & RxMetrics & {
  type: 'rx';
  contact: Contact;
}

/**
 * Main data structure for Rx Sales Data aggregation.
 * Represents sales data for a specific product and period, with different
 * target types (brick, account, or contact) and their associated metrics.
 */
export type RxSalesData = BrickSalesData | DirectSalesData | RxData;
